import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { PhotoData, PhotoCaptureState, PDFOptions, ExternalApiPayload, PDFData } from '../../types/photo';
import pdfService from '../../services/pdfService';
import externalApiService from '../../services/externalApiService';
import photoService from '../../services/photoService';
import { RootState } from '../store';

const initialState: PhotoCaptureState = {
  isCapturing: false,
  isConverting: false,
  isUploading: false,
  isUploadingPDF: false,
  currentPhoto: null,
  currentPDF: null,
  error: null,
  success: null,
};

// Async thunks
export const capturePhoto = createAsyncThunk(
  'photo/capturePhoto',
  async (_, { rejectWithValue }) => {
    try {
      if (!photoService.isCameraSupported()) {
        throw new Error('Camera not supported on this device');
      }

      const settings = photoService.getDefaultCameraSettings();
      const stream = await photoService.getCameraStream(settings);
      const photoData = await photoService.capturePhotoFromStream(stream);
      
      // Stop the stream after capture
      photoService.stopCameraStream(stream);
      
      // Process the photo
      const processedPhoto = await photoService.processPhoto(photoData);
      
      return processedPhoto;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to capture photo');
    }
  }
);

export const uploadPDF = createAsyncThunk(
  'photo/uploadPDF',
  async (file: File, { rejectWithValue }) => {
    try {
      // Process PDF upload
      const pdfData = await photoService.processPDFUpload(file);
      return pdfData;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to upload PDF');
    }
  }
);

export const convertPhotoToPDF = createAsyncThunk(
  'photo/convertPhotoToPDF',
  async (options: PDFOptions, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentPhoto } = state.photo;

      if (!currentPhoto) {
        throw new Error('No photo available for conversion');
      }

      // Use enhanced PDF options for better OCR quality
      const enhancedOptions: PDFOptions = {
        ...options,
        dpi: 300, // High DPI for better OCR
        imageQuality: 0.95, // High image quality for OCR
        compression: 'jpeg' // JPEG for good quality/size balance
      };

      const pdfBase64 = await pdfService.convertImageToPDF(currentPhoto, enhancedOptions);
      return pdfBase64;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to convert photo to PDF');
    }
  }
);

export const uploadPDFToExternalAPI = createAsyncThunk(
  'photo/uploadPDFToExternalAPI',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentPhoto, currentPDF } = state.photo;
      const { user } = state.auth;

      if (!currentPhoto && !currentPDF) {
        throw new Error('No document available for upload');
      }

      if (!user) {
        throw new Error('User not authenticated');
      }

      let pdfBase64: string;
      let fileName: string;
      let fileSize: number;

      if (currentPDF) {
        // Upload PDF directly - use stored file size since we have it
        pdfBase64 = await photoService.convertPDFToBase64(currentPDF);
        fileName = currentPDF.fileName;
        fileSize = currentPDF.fileSize; // Use stored file size
      } else if (currentPhoto) {
        // Convert photo to PDF first with enhanced quality
        const enhancedOptions: PDFOptions = {
          orientation: 'portrait',
          format: 'a4',
          margin: 10,
          includeMetadata: true,
          dpi: 300, // High DPI for better OCR
          imageQuality: 0.95, // High image quality for OCR
          compression: 'jpeg' // JPEG for good quality/size balance
        };
        
        pdfBase64 = await pdfService.convertImageToPDF(currentPhoto, enhancedOptions);
        fileName = currentPhoto.fileName.replace('.jpg', '.pdf');
        fileSize = pdfBase64.length;
      } else {
        throw new Error('No document available for upload');
      }
      
      // Prepare payload for webhook
      const payload: ExternalApiPayload = {
        userId: user.id,
        userEmail: user.email,
        userName: `${user.firstName} ${user.lastName}`,
        timestamp: new Date().toISOString(),
        pdfBase64,
        fileName,
        fileSize,
      };

      // Send to webhook
      const response = await externalApiService.sendPDFToExternalAPI(payload);
      
      // The webhook returns {message: 'Workflow was started'} on success
      // Since we got here without an error, the webhook call was successful
      // We don't need to check response.success as the webhook doesn't return that property
      
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to send to webhook');
    }
  }
);

export const testExternalAPIConnection = createAsyncThunk(
  'photo/testConnection',
  async (_, { rejectWithValue }) => {
    try {
      const isConnected = await externalApiService.testConnection();
      return isConnected;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Connection test failed');
    }
  }
);

export const testN8nServer = createAsyncThunk(
  'photo/testN8nServer',
  async (_, { rejectWithValue }) => {
    try {
      const isConnected = await externalApiService.testN8nServer();
      return isConnected;
    } catch (error: any) {
      return rejectWithValue(error.message || 'n8n server test failed');
    }
  }
);

const photoSlice = createSlice({
  name: 'photo',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
    resetState: (state) => {
      state.isCapturing = false;
      state.isConverting = false;
      state.isUploading = false;
      state.isUploadingPDF = false;
      state.currentPhoto = null;
      state.currentPDF = null;
      state.error = null;
      state.success = null;
    },
    setCurrentPhoto: (state, action: PayloadAction<PhotoData>) => {
      state.currentPhoto = action.payload;
      state.currentPDF = null; // Clear PDF when photo is set
    },
    setCurrentPDF: (state, action: PayloadAction<PDFData>) => {
      state.currentPDF = action.payload;
      state.currentPhoto = null; // Clear photo when PDF is set
    },
    clearCurrentPhoto: (state) => {
      state.currentPhoto = null;
    },
    clearCurrentPDF: (state) => {
      state.currentPDF = null;
    },
    clearSuccessAfterDelay: (state) => {
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Capture photo
      .addCase(capturePhoto.pending, (state) => {
        state.isCapturing = true;
        state.error = null;
        state.success = null;
      })
      .addCase(capturePhoto.fulfilled, (state, action) => {
        state.isCapturing = false;
        state.currentPhoto = action.payload;
        state.currentPDF = null; // Clear any existing PDF
        state.success = 'Photo captured successfully';
      })
      .addCase(capturePhoto.rejected, (state, action) => {
        state.isCapturing = false;
        state.error = action.payload as string;
      })
      
      // Upload PDF
      .addCase(uploadPDF.pending, (state) => {
        state.isUploadingPDF = true;
        state.error = null;
        state.success = null;
      })
      .addCase(uploadPDF.fulfilled, (state, action) => {
        state.isUploadingPDF = false;
        state.currentPDF = action.payload;
        state.currentPhoto = null; // Clear any existing photo
        state.success = 'PDF uploaded successfully';
      })
      .addCase(uploadPDF.rejected, (state, action) => {
        state.isUploadingPDF = false;
        state.error = action.payload as string;
      })
      
      // Convert to PDF
      .addCase(convertPhotoToPDF.pending, (state) => {
        state.isConverting = true;
        state.error = null;
      })
      .addCase(convertPhotoToPDF.fulfilled, (state) => {
        state.isConverting = false;
        state.success = 'Photo converted to PDF successfully';
      })
      .addCase(convertPhotoToPDF.rejected, (state, action) => {
        state.isConverting = false;
        state.error = action.payload as string;
      })
      
      // Upload to external API
      .addCase(uploadPDFToExternalAPI.pending, (state) => {
        state.isUploading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(uploadPDFToExternalAPI.fulfilled, (state, action) => {
        state.isUploading = false;
        state.success = `PDF uploaded successfully: ${action.payload.message}`;
        // Clear current photo/PDF after successful upload
        state.currentPhoto = null;
        state.currentPDF = null;
      })
      .addCase(uploadPDFToExternalAPI.rejected, (state, action) => {
        state.isUploading = false;
        state.error = action.payload as string;
      })
      
      // Test connection
      .addCase(testExternalAPIConnection.fulfilled, (state, action) => {
        if (action.payload) {
          state.success = 'External API connection successful';
        } else {
          state.error = 'External API connection failed';
        }
      })
      .addCase(testExternalAPIConnection.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

// Export actions
export const {
  clearError,
  clearSuccess,
  resetState,
  setCurrentPhoto,
  setCurrentPDF,
  clearCurrentPhoto,
  clearCurrentPDF,
  clearSuccessAfterDelay,
} = photoSlice.actions;

// Export selectors
export const selectPhotoState = (state: RootState) => state.photo;
export const selectCurrentPhoto = (state: RootState) => state.photo.currentPhoto;
export const selectCurrentPDF = (state: RootState) => state.photo.currentPDF;
export const selectIsCapturing = (state: RootState) => state.photo.isCapturing;
export const selectIsConverting = (state: RootState) => state.photo.isConverting;
export const selectIsUploading = (state: RootState) => state.photo.isUploading;
export const selectIsUploadingPDF = (state: RootState) => state.photo.isUploadingPDF;
export const selectPhotoError = (state: RootState) => state.photo.error;
export const selectPhotoSuccess = (state: RootState) => state.photo.success;

export default photoSlice.reducer;
